import React, { useState, useEffect, useRef, useContext, useReducer, useDeferredValue } from "react"
import _ from "lodash"
import CardCareers from "../cards/cards-careers"
import "../algolia/algolia.css"
import "../algolia/algolia-explore.css"
import { useQueryParam, JsonParam, NumberParam, BooleanParam, StringParam } from "use-query-params"
import Link from "../../link"
import { FirebaseContext } from "../../components/Firebase"
import Pagination from "react-js-pagination"
import FuzzySearch from "fuzzy-search"
import LoadingSpinner from "../../components/loading/LoadingSpinner.js"
import { removeMissingCareers, calcMatchStrength } from "../../components/assessment/utils/utils.js"
import OutsideClickHandler from "react-outside-click-handler"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons"
import EyeBrow from "../eyebrow/eyebrow"
import { FormattedMessage, useIntl } from "react-intl"
import { useCareers } from "../assessment/useCareers.js"
import { SelectButton } from "primereact/selectbutton"
import { RadioButton } from "primereact/radiobutton"
import { getSchoolSpecificCareers } from "../../components/assessment/getSchoolSpecificCareers.js"
import { WingsButton, WingsCareerCard } from "@wingspans-ui"

const HITS_PER_PAGE = 9

const normalizeText = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeText(item)).join(" ")
  }

  if (value && typeof value === "object") {
    return normalizeText(Object.values(value))
  }

  if (value === null || value === undefined) {
    return ""
  }

  return String(value).toLowerCase().trim()
}

const careerMatchesSearchTokens = (career, query) => {
  const normalizedQuery = normalizeText(query)
  if (!normalizedQuery) {
    return true
  }

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean)
  if (!tokens.length) {
    return true
  }

  const searchableText = normalizeText([
    career?.title,
    career?.career_keywords,
    career?.career_job_categories,
    career?.career_pathways_filter,
    career?.career_education_level,
  ])

  return tokens.every((token) => searchableText.includes(token))
}

const searchCareers = (items, query) => {
  const normalizedQuery = (query || "").trim()
  if (!normalizedQuery) {
    return items
  }

  const searcher = new FuzzySearch(items, ["title"], {
    caseSensitive: false,
  })

  const fuzzyResults = searcher.search(normalizedQuery)
  const strictTokenMatches = fuzzyResults.filter((career) => careerMatchesSearchTokens(career, normalizedQuery))

  if (strictTokenMatches.length === 0 && normalizedQuery.length >= 2) {
    return fuzzyResults
  }

  return strictTokenMatches
}

// reducer used to track state of career explore component and handle actions regarding pagination, search, and filters
const exploreReducer = (state, action) => {
  // incoming payloads
  const { newPage, newSearchState, newAllMatches, newStartingPage, isSchoolFilterOn } = action
  // inital state

  // activePage: 1,
  // searchState: "",
  // allMatches: [],
  // displayMatches: []

  switch (action.type) {
    case "SET_PAGE":
      const startingPoint = (newPage - 1) * HITS_PER_PAGE
      const endingPoint = startingPoint + HITS_PER_PAGE
      const subset = state.filteredMatches.slice(startingPoint, endingPoint)
      return { ...state, activePage: newPage, displayMatches: subset }
    case "SET_SEARCH_STATE":
      const results = searchCareers(state.allMatches, newSearchState)

      const displayResults = results.slice(0, HITS_PER_PAGE)
      return {
        ...state,
        searchState: newSearchState,
        filteredMatches: results,
        displayMatches: displayResults,
        activePage: 1,
      }
    case "LOAD_ALL_MATCHES":
      const filteredMatches = searchCareers(newAllMatches, newSearchState)
      const initialStartingPoint = (newStartingPage - 1) * HITS_PER_PAGE
      const initialEndingPoint = initialStartingPoint + HITS_PER_PAGE
      const initialSubset = filteredMatches.slice(initialStartingPoint, initialEndingPoint)

      return {
        ...state,
        allMatches: newAllMatches,
        filteredMatches: filteredMatches,
        activePage: newStartingPage,
        displayMatches: initialSubset,
      }
    case "SET_SCHOOL_FILTER":
      return {
        ...state,
        isSchoolFilterOn: isSchoolFilterOn,
      }

    default:
      throw new Error("Action type not set")
  }
}

/*
 * title = The title of the page. ie - Financial Manager, Architect, etc.
 * filterReference = The Algolia filter reference. ie - story_school_reference
 * classContext = A class to override default styles. ie - career, company, major, etc.
 */

const CareersPersonalityExplore = (props) => {
  const { locale, formatMessage } = useIntl()
  const { profile, subdomainConfig, firebase } = useContext(FirebaseContext)
  const allCosmicjsCareers = useCareers(locale)
  const { subdomainPetersonsInstitutionId, subdomainSchoolName } = subdomainConfig

  const [state, dispatch] = useReducer(exploreReducer, {
    activePage: 1,
    searchState: "",
    allMatches: [],
    filteredMatches: [],
    displayMatches: [],
    isSchoolFilterOn: subdomainPetersonsInstitutionId ? true : false,
  })

  const { activePage, searchState, displayMatches, filteredMatches, isSchoolFilterOn } = state
  const [searchInput, setSearchInput] = useState("")
  const deferredSearchInput = useDeferredValue(searchInput)
  const [schoolFilterValueIndex, setSchoolFilterValueIndex] = useState(isSchoolFilterOn ? 0 : 1)
  const [schoolSpecificCareers, setSchoolSpecificCareers] = useState([])
  const [isLoadingScreen, setIsLoadingScreen] = useState(false)
  const [dislikedCareerSlugs, setDislikedCareerSlugs] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [isTaleTaken, setIsTaleTaken] = useState(false)
  const [personalityQuery, setPersonalityQuery] = useQueryParam("personality", BooleanParam)
  const [changeCourseCareers, setChangeCourseCareers] = useState([])
  const [staySteadyCareers, setSteadySteadyCareers] = useState([])

  // Track which filter options have results
  const hasPersonalityMatches = profile?.assessmentRecord?.careerMatches && profile.assessmentRecord.careerMatches.length > 0
  const hasSteadySteadyMatches = staySteadyCareers.length > 0
  const hasChangeCourseMatches = changeCourseCareers.length > 0
  const hasCombinedMatches = hasPersonalityMatches || hasSteadySteadyMatches || hasChangeCourseMatches

  const [filters, setFilters] = useState({
    salary: {},
    education: {},
    dress: {},
    activity: {},
  })

  const [pageParam, setPageParam] = useQueryParam("page", NumberParam)
  const [queryFilters, setQueryFilters] = useQueryParam("queryFilters", JsonParam)
  const [schoolFilterParam, setSchoolFilterParam] = useQueryParam("schoolFilter", BooleanParam)
  const [matchTypeParam, setMatchTypeParam] = useQueryParam("matchType", StringParam)
  const [, setCategoryQuery] = useQueryParam("category", StringParam)
  const [, setShowSavedQuery] = useQueryParam("showSaved", BooleanParam)

  const matchTypeFilter = matchTypeParam ?? "combined"

  const ref = useRef(null)

  /**
   * Select button display option to toggle school filtering
   */
  const schoolFilterOptions = [`${subdomainSchoolName}`, locale === "es-ES" ? "Todos los Programas" : "All Programs"]

  /**
   * handles school filter change when checkboxes toggled, updates URL, and sets state
   */
  const handleSchoolFilterChange = (e) => {
    const value = e.value
    setSchoolFilterValueIndex(schoolFilterOptions.indexOf(value))
    setQueryFilters(null)
    setPageParam(1)

    if (value === "All Programs" || "Todos los Programas") {
      // setDisplayCareers(allCosmicjsCareers)
      dispatch({
        type: "SET_SCHOOL_FILTER",
        isSchoolFilterOn: false,
      })
    } else {
      dispatch({
        type: "SET_SCHOOL_FILTER",
        isSchoolFilterOn: true,
      })
    }

    setSchoolFilterParam(value === "All Programs" || value === "Todos los Programas" ? false : true)
  }

  const handlePageChange = (pageNumber) => {
    if (pageNumber !== activePage) {
      scrollToElementWithOffset(ref.current, 225, "smooth")
      setPageParam(pageNumber)
      dispatch({
        type: "SET_PAGE",
        newPage: pageNumber,
      })
    }
  }

  /**
   * opens and closes filter dropdown
   **/
  const handleFiltersOnClick = () => {
    if (!showFilters) {
      setShowFilters(true)
    } else {
      handleOutsideFilterClick()
      setShowFilters(false)
    }
  }

  /**
   * handles toggle off of showFilters and updates URL when clicking outside of filter dropdown
   * @param {*} e
   * @returns
   */
  const handleOutsideFilterClick = (e) => {
    // prevents click from firing when clicking scroll bar
    if (e?.target?.tagName === "HTML") return

    // reformat filters object into queryFilters refinementList format
    const newRefinements = Object.entries(filters).reduce((acc, [key, valueObj]) => {
      const trueValues = Object.keys(valueObj).filter((k) => valueObj[k])

      if (trueValues.length > 0) {
        acc[key] = trueValues
      }

      return acc
    }, {})

    // update `queryFilters` with the new refinement lists
    const updatedQueryFilters = {
      ...queryFilters,
      refinementList: newRefinements,
    }

    // check to see if filters changed, prevents URL setting when they are the same as current params
    const queryRefinementList = queryFilters ? queryFilters.refinementList : {}
    const filtersHaveChanged = !_.isEqual(queryRefinementList, updatedQueryFilters.refinementList) && !_.isEmpty(updatedQueryFilters.refinementList)

    // use the setQueryFilters to update the state
    showFilters && filtersHaveChanged && setQueryFilters(updatedQueryFilters)

    setShowFilters(false)
  }

  /**
   * updates filters state when checkbox is toggled and resets page to 1
   */
  const updateFilters = (filterType, filter, isChecked) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterType]: {
        ...prevFilters[filterType],
        [filter]: isChecked,
      },
    }))
    setPageParam(1)
  }

  const hasSelectedFilters = Object.values(queryFilters?.refinementList || {}).some(
    (selectedValues) => Array.isArray(selectedValues) && selectedValues.length > 0
  )

  const clearFilters = () => {
    if (!hasSelectedFilters) {
      return
    }

    const clearedFilters = {
      salary: {},
      education: {},
      dress: {},
      activity: {},
    }

    setFilters(clearedFilters)
    setPageParam(1)
    setQueryFilters({
      ...(queryFilters || {}),
      page: 1,
      refinementList: {},
    })
  }

  const inSalaryFilters = (career) => {
    const salaryDict = {
      "Not that Important": [0, 51000],
      Important: [51000, 150000],
      "Very Important": [150000, Infinity],
    }

    for (const salaryFilter in filters.salary) {
      if (filters.salary[salaryFilter]) {
        if (salaryDict[salaryFilter][0] <= career.career_salary_median && career.career_salary_median < salaryDict[salaryFilter][1]) {
          return true
        }
      }
    }

    return false
  }

  const inEducationFilters = (career) => {
    for (const educationFilter in filters.education) {
      if (filters.education[educationFilter]) {
        if (career.career_education_level?.includes(educationFilter)) {
          return true
        }
      }
    }

    return false
  }

  const inDressFilters = (career) => {
    for (const dressFilter in filters.dress) {
      if (filters.dress[dressFilter]) {
        if (career.career_dress == dressFilter) {
          return true
        }
      }
    }
  }

  const inActivityFilters = (career) => {
    for (const activityFilter in filters.activity) {
      if (filters.activity[activityFilter]) {
        if (career.career_activity == activityFilter) {
          return true
        }
      }
    }
  }

  useEffect(() => {
    if (!firebase) return
    const fetchSavedResponses = async () => {
      const savedResponses = await firebase.getUserTaleResponsesAndMatches()
      const { data } = savedResponses || {}
      const { taleResponses, careerMatches } = data || {}
      const { changeCourseCareers = [], staySteadyCareers = [] } = careerMatches || {}

      console.log('data', data)

      // Store career match data
      setChangeCourseCareers(changeCourseCareers)
      setSteadySteadyCareers(staySteadyCareers)

      if (taleResponses) {
        // should do extra validation here
        // if the responses indicate submission, set progress status accordingly
        const { isSubmitted } = taleResponses
        if (isSubmitted === true) {
          console.log("savedResponses", data)
          setIsTaleTaken(true)
        } else if (isSubmitted === false) {
          setIsTaleTaken(false)
        }
      }
    }
    fetchSavedResponses()
  }, [firebase])

  useEffect(() => {
    setDislikedCareerSlugs(profile?.assessmentRecord?.dislikedCareers || [])
  }, [profile])

  useEffect(() => {
    if (deferredSearchInput === searchState) {
      return
    }

    dispatch({
      type: "SET_SEARCH_STATE",
      newSearchState: deferredSearchInput,
    })
  }, [deferredSearchInput, searchState])

  // triggers when filters, disliked careers, or school filter changes, used to reset state
  useEffect(() => {
    let filteredCareers = allCosmicjsCareers.filter((career) => !dislikedCareerSlugs.includes(career.slug))

    // filters out careers that are not in the school specific careers list
    if (isSchoolFilterOn) {
      filteredCareers = filteredCareers.filter((career) => schoolSpecificCareers.some((schoolCareer) => schoolCareer.slug === career.slug))
    }

    // checks if at least one filter is applied
    const hasAtLeastOneFilter = Object.values(filters).some((filterType) => Object.values(filterType).some((filter) => filter))

    if (hasAtLeastOneFilter) {
      filteredCareers = filteredCareers.filter((career) => {
        return inSalaryFilters(career) || inEducationFilters(career) || inDressFilters(career) || inActivityFilters(career)
      })
    }

    // Handle different match type scenarios
    let currentCosmicCareers = []

    if (matchTypeFilter === "stay steady") {
      // Show only stay steady careers from TAL-E
      const filteredCareersBySlug = new Map(filteredCareers.map(career => [career.slug, career]))
      currentCosmicCareers = staySteadyCareers
        .map((career) => filteredCareersBySlug.get(career.slug))
        .filter(Boolean)
        .map((career) => ({ ...career, slug: career.slug }))
    } else if (matchTypeFilter === "change course") {
      // Show only change course careers from TAL-E
      const filteredCareersBySlug = new Map(filteredCareers.map(career => [career.slug, career]))
      currentCosmicCareers = changeCourseCareers
        .map((career) => filteredCareersBySlug.get(career.slug))
        .filter(Boolean)
        .map((career) => ({ ...career, slug: career.slug }))
    } else if (matchTypeFilter === "combined") {
      // For combined, show both personality matches and TAL-E matches (no duplicates)
      const allCareerSlugs = new Set()
      
      // Create maps for TAL-E careers
      const staySteadyMap = new Map(staySteadyCareers.map(c => [c.slug, c]))
      const changeCourseMap = new Map(changeCourseCareers.map(c => [c.slug, c]))
      
      // Collect all careers with their metadata
      const careersWithMetadata = []
      
      // Add personality matches if they exist
      if (profile?.assessmentRecord?.careerMatches) {
        const personalityCareers = removeMissingCareers(profile.assessmentRecord.careerMatches, filteredCareers)
        personalityCareers.forEach(career => {
          if (!allCareerSlugs.has(career.slug)) {
            const matchQuality = calcMatchStrength(career.correlation)
            const isSteadySteady = staySteadyMap.has(career.slug)
            const isChangeCourse = changeCourseMap.has(career.slug)
            const recommendation = isSteadySteady ? 'stay steady' : (isChangeCourse ? 'change course' : null)
            
            careersWithMetadata.push({
              ...career,
              matchQuality,
              recommendation,
              hasPersonalityMatch: true
            })
            allCareerSlugs.add(career.slug)
          }
        })
      }
      
      // Add TAL-E matches that aren't already included (no personality match)
      const taleCareerSlugs = [...staySteadyCareers.map(c => c.slug), ...changeCourseCareers.map(c => c.slug)]
      const taleCareers = filteredCareers.filter(career => taleCareerSlugs.includes(career.slug))
      
      taleCareers.forEach(career => {
        if (!allCareerSlugs.has(career.slug)) {
          const isSteadySteady = staySteadyMap.has(career.slug)
          const recommendation = isSteadySteady ? 'stay steady' : 'change course'
          
          careersWithMetadata.push({
            ...career,
            matchQuality: null,
            recommendation,
            hasPersonalityMatch: false
          })
          allCareerSlugs.add(career.slug)
        }
      })
      
      // Sort by the specified hierarchy
      // Careers with TAL-E recommendations (stay steady or change course) come first,
      // then careers without recommendations
      const qualityOrder = { 'Excellent': 0, 'Good': 1, 'Fair': 2, 'Poor': 3, null: 4 }
      const recommendationOrder = { 'stay steady': 0, 'change course': 1, null: 2 }
      
      careersWithMetadata.sort((a, b) => {
        // Separate careers with recommendations from those without
        const aHasRecommendation = a.recommendation !== null
        const bHasRecommendation = b.recommendation !== null
        
        // Careers with recommendations always come before careers without
        if (aHasRecommendation && !bHasRecommendation) return -1
        if (!aHasRecommendation && bHasRecommendation) return 1
        
        // If both have recommendations (or both don't), sort by match quality
        const qualityDiff = qualityOrder[a.matchQuality] - qualityOrder[b.matchQuality]
        if (qualityDiff !== 0) return qualityDiff
        
        // Then by recommendation type (stay steady before change course)
        return recommendationOrder[a.recommendation] - recommendationOrder[b.recommendation]
      })
      
      currentCosmicCareers = careersWithMetadata
    } else if (matchTypeFilter === "personality") {
      // Show only personality-based matches
      if (profile?.assessmentRecord?.careerMatches) {
        currentCosmicCareers = removeMissingCareers(profile.assessmentRecord.careerMatches, filteredCareers)
      }
    }

    let startingPage = pageParam || activePage || 1

    dispatch({
      type: "LOAD_ALL_MATCHES",
      newAllMatches: currentCosmicCareers,
      newStartingPage: startingPage,
      newSearchState: searchState,
    })
  }, [filters, dislikedCareerSlugs, schoolSpecificCareers, isSchoolFilterOn, matchTypeFilter, changeCourseCareers, staySteadyCareers])

  // syncs page with URL, filteredMatches load async so need to wait for them to load before page set
  useEffect(() => {
    if (pageParam) {
      dispatch({
        type: "SET_PAGE",
        newPage: pageParam,
      })
    }
  }, [pageParam, filteredMatches])

  // syncs school filtering with URL
  useEffect(() => {
    if (schoolFilterParam === true) {
      setSchoolFilterValueIndex(0)

      dispatch({
        type: "SET_SCHOOL_FILTER",
        isSchoolFilterOn: true,
      })
    } else if (schoolFilterParam === false) {
      setSchoolFilterValueIndex(1)

      dispatch({
        type: "SET_SCHOOL_FILTER",
        isSchoolFilterOn: false,
      })
    }
  }, [schoolFilterParam])

  // syncs filters with queryFilters if url changes
  useEffect(() => {
    let newFilters = {
      salary: {},
      education: {},
      dress: {},
      activity: {},
    }

    // parse queryFilters to update filters
    for (let key in queryFilters?.refinementList) {
      if (newFilters[key]) {
        queryFilters.refinementList[key].forEach((filterValue) => {
          newFilters[key][filterValue] = true
        })
      }
    }

    setFilters(newFilters)
  }, [queryFilters])

  // will display loading screen and scroll to component, need to use timeout to give loading time and mask scrolling
  // only fires if state is passed to link from "see more matches" button
  // looking for better solution
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window.history.state?.scrollToExplore === true || pageParam) {
        setIsLoadingScreen(true)
        setTimeout(() => {
          ref.current !== null && scrollToElementWithOffset(ref.current, 225, "auto")
        }, 1000)
        setTimeout(() => {
          setIsLoadingScreen(false)
        }, 1000)
      }
    }
  }, [ref.current])

  useEffect(() => {
    // gets school specific careers based on petersons majors data and cosmicjs careers which have linked majors
    if (!!firebase) {
      const getSchoolData = async () => {
        // get petersons school data, might be nothing
        const res = await firebase.getPetersonsSchoolData(subdomainPetersonsInstitutionId)
        const { majorsData = [] } = res?.data || {}

        const schoolSpecificCareers = getSchoolSpecificCareers(allCosmicjsCareers, majorsData)
        setSchoolSpecificCareers(schoolSpecificCareers)
      }

      getSchoolData()
    }
  }, [firebase])

  /**
   * Maps over the displayMatches and returns a list of WingsCareerCard components, if the career is found in allCosmicjsCareers else returns null
   **/
  const FilteredMatchCards = displayMatches
    .map((orderedCareer) => {
      // find and merge career with career ID and correlation
      const foundCareer = allCosmicjsCareers.find((allCareer) => allCareer.slug === orderedCareer.slug)

      if (foundCareer) {
        // Only show correlation for combined and personality filters, not for stay steady or change course
        const showCorrelation = matchTypeFilter !== "stay steady" && matchTypeFilter !== "change course"
        
        // Determine recommendation label
        let recommendationLabel = null
        if (matchTypeFilter === "combined" && orderedCareer.recommendation) {
          recommendationLabel = orderedCareer.recommendation
        } else if (matchTypeFilter === "stay steady") {
          recommendationLabel = "stay steady"
        } else if (matchTypeFilter === "change course") {
          recommendationLabel = "change course"
        }
        
        return (
          <li className="ais-Hits-item" key={orderedCareer.id}>
            <WingsCareerCard
              objectID={orderedCareer.id}
              img={foundCareer.career_banner_image}
              title={foundCareer.title}
              slug={foundCareer.slug}
              demand={foundCareer.career_demand}
              category={foundCareer?.career_job_categories?.title}
              contentType={"careers"}
              salaryEntry={foundCareer.career_salary_entry_level}
              salaryExperienced={foundCareer.career_salary_experienced}
              correlation={showCorrelation ? orderedCareer.correlation : undefined}
              onDislike={slug => setDislikedCareerSlugs(prev => [...prev, slug])}
              recommendationLabel={recommendationLabel}
            />
          </li>
        )
      } else {
        return null
      }
    })
    .filter((match) => match !== null)

  return (
    <>
      {isLoadingScreen && <LoadingSpinner />}
      <div
        className="ais-SearchBox algolia-careers-personality-explore w-justify-self-end w-justify-end"
      >
        <form
          noValidate=""
          className="ais-SearchBox-form w-full algolia-careers-personality-search-form"
          action=""
          role="search"
          onSubmit={(e) => e.preventDefault()}
        >
          <input
            type="search"
            placeholder={formatMessage({ id: "algolia-careers-personality-search-by-job-title", defaultMessage: "Search by job title" })}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            required=""
            maxLength="512"
            className="ais-SearchBox-input"
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ maxHeight: "42px" }}
            value={searchInput}
          ></input>
          <button type="submit" title="Submit your search query." className="ais-SearchBox-submit" style={{ height: "42px" }}>
            <svg
              aria-hidden="true"
              focusable="false"
              data-prefix="far"
              data-icon="search"
              className="svg-inline--fa fa-search fa-w-16 "
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
            >
              <path
                fill="currentColor"
                d="M508.5 468.9L387.1 347.5c-2.3-2.3-5.3-3.5-8.5-3.5h-13.2c31.5-36.5 50.6-84 50.6-136C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c52 0 99.5-19.1 136-50.6v13.2c0 3.2 1.3 6.2 3.5 8.5l121.4 121.4c4.7 4.7 12.3 4.7 17 0l22.6-22.6c4.7-4.7 4.7-12.3 0-17zM208 368c-88.4 0-160-71.6-160-160S119.6 48 208 48s160 71.6 160 160-71.6 160-160 160z"
              ></path>
            </svg>
          </button>
          {searchInput.length > 0 && (
            <button
              type="reset"
              onClick={() => setSearchInput("")}
              title="Clear the search query."
              className="ais-SearchBox-reset"
              hidden=""
            >
              <svg
                aria-hidden="true"
                focusable="false"
                data-prefix="fas"
                data-icon="times"
                className="svg-inline--fa fa-times fa-w-11 "
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 352 512"
              >
                <path
                  fill="currentColor"
                  d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"
                ></path>
              </svg>
            </button>
          )}
        </form>
        {subdomainPetersonsInstitutionId && (
          <div className="results-school-toggle">
            <SelectButton
              value={schoolFilterOptions[schoolFilterValueIndex]}
              options={schoolFilterOptions}
              onChange={handleSchoolFilterChange}
              className="no-transition-selectbutton"
            />
          </div>
        )}
      </div>

      <div className="ais-ScrollTo">
      <div className="w-default-algolia-card-hits-container stream stream-careers stream-careers-explore stream-careers-explore-personality" ref={ref}>
        <OutsideClickHandler onOutsideClick={handleOutsideFilterClick}>
          <div className="stream-explore-filters stream-explore-filters-matches">


            <div className={`explore-context-filter ${showFilters ? "show" : "hide"}`}>
              <h3>
                <FormattedMessage id="algolia-careers-personality-filter-careers-by" defaultMessage="Filter Careers by" />:
              </h3>
              <div className="filter filter-four">
                <EyeBrow
                  text={<FormattedMessage id="results-filter-salary" defaultMessage="Salary" />}
                  style={{
                    marginBottom: "1.5rem",
                  }}
                />
                <div className="ais-RefinementList">
                  <ul className="ais-RefinementList-list">
                    {salaryOptions.map((option, index) => (
                      <li key={index} className="ais-RefinementList-item">
                        <label className="ais-RefinementList-label">
                          <input
                            className="ais-RefinementList-checkbox"
                            type="checkbox"
                            checked={filters.salary[option.value] || false}
                            onChange={(event) => updateFilters("salary", option.value, event.target.checked)}
                          />
                          <span className="ais-RefinementList-labelText">{option.text}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="filter filter-four">
                <EyeBrow
                  text={<FormattedMessage id="results-filter-education" defaultMessage="Education" />}
                  style={{
                    marginBottom: "1.5rem",
                  }}
                />
                <ul className="ais-RefinementList-list">
                  {educationOptions.map((option, index) => (
                    <li key={index} className="ais-RefinementList-item">
                      <label className="ais-RefinementList-label">
                        <input
                          className="ais-RefinementList-checkbox"
                          type="checkbox"
                          checked={filters.education[option.value] || false}
                          onChange={(event) => updateFilters("education", option.value, event.target.checked)}
                        />
                        <span className="ais-RefinementList-labelText">{option.text}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="filter filter-four">
                <EyeBrow
                  text={<FormattedMessage id="results-filter-dress" defaultMessage="Dress" />}
                  style={{
                    marginBottom: "1.5rem",
                  }}
                />
                <ul className="ais-RefinementList-list">
                  {dressOptions.map((option, index) => (
                    <li key={index} className="ais-RefinementList-item">
                      <label className="ais-RefinementList-label">
                        <input
                          className="ais-RefinementList-checkbox"
                          type="checkbox"
                          checked={filters.dress[option.value] || false}
                          onChange={(event) => updateFilters("dress", option.value, event.target.checked)}
                        />
                        <span className="ais-RefinementList-labelText">{option.text}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="filter filter-four filter-last">
                <EyeBrow
                  text={<FormattedMessage id="algolia-careers-personality-physical-activity" defaultMessage="Physical Activity" />}
                  style={{
                    marginBottom: "1.5rem",
                  }}
                />
                <ul className="ais-RefinementList-list">
                  {activityOptions.map((option, index) => (
                    <li key={index} className="ais-RefinementList-item">
                      <label className="ais-RefinementList-label">
                        <input
                          className="ais-RefinementList-checkbox"
                          type="checkbox"
                          checked={filters.activity[option.value] || false}
                          onChange={(event) => updateFilters("activity", option.value, event.target.checked)}
                        />
                        <span className="ais-RefinementList-labelText">{option.text}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="ais-ClearRefinements">
                <WingsButton
                  variant="secondary"
                  size="small"
                  className={`ais-ClearRefinements-button ${!hasSelectedFilters ? "ais-ClearRefinements-button--disabled" : ""}`}
                  onClick={clearFilters}
                  disabled={!hasSelectedFilters}
                >
                  {formatMessage({ id: "careers-explore-clear-filters", defaultMessage: "Clear Filters" })}
                </WingsButton>
              </div>
            </div>

            <div>
              <div style={{display: 'flex', justifyContent: 'flex-start'}}>
              <ul className="explore-filters-list">
                <li className="explore-filters-list-item explore-filters-list-item-sort">
                  <FormattedMessage id="sort-by" defaultMessage="Sort by" />:
                </li>
                <li className="explore-filters-list-item explore-filters-list-item-newest">
                  <a
                    href="/"
                    onClick={e => {
                      e.preventDefault()
                      setPersonalityQuery(undefined)
                      setCategoryQuery("Careers")
                      setShowSavedQuery(undefined)
                    }}
                  >
                    <FormattedMessage id="all" defaultMessage="All" />
                  </a>
                </li>

                <li className="explore-filters-list-item explore-filters-list-item-newest">
                  <Link to="/dashboard?category=PersonalityCareers&personality=true" className={"active"}>
                    <FormattedMessage id="matches" defaultMessage="Matches" />
                  </Link>
                </li>

                <li className="explore-filters-list-item explore-filters-list-item-saved">
                  <a
                    href="/"
                    onClick={e => {
                      e.preventDefault()
                      setPersonalityQuery(undefined)
                      setCategoryQuery("Careers")
                      setShowSavedQuery(true)
                    }}
                  >
                    <FormattedMessage id="algolica-careers-explore-saved" defaultMessage="Saved" />
                  </a>
                </li>
              </ul>
              </div>


                <div className="results-match-type-toggle" style={{ marginTop: "1rem" }}>
                  <div style={{ display: "flex", flexDirection: "row", columnGap: "2rem", rowGap: '1rem', flexWrap: "wrap" }}>
                    {hasCombinedMatches && (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <RadioButton
                          inputId="matchType1"
                          name="matchType"
                          value="combined"
                          checked={matchTypeFilter === "combined"}
                          onChange={(e) => {
                            setMatchTypeParam(e.value, "replaceIn")
                            setPageParam(1, "replaceIn")
                          }}
                        />
                        <label htmlFor="matchType1" style={{ cursor: "pointer", marginBottom: 0 }}>
                          {locale === "es-ES" ? "Combinado" : "Combined"}
                        </label>
                      </div>
                    )}
                    {hasPersonalityMatches && (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <RadioButton
                          inputId="matchType2"
                          name="matchType"
                          value="personality"
                          checked={matchTypeFilter === "personality"}
                          onChange={(e) => {
                            setMatchTypeParam(e.value, "replaceIn")
                            setPageParam(1, "replaceIn")
                          }}
                        />
                        <label htmlFor="matchType2" style={{ cursor: "pointer", marginBottom: 0 }}>
                          <FormattedMessage
                            id="career-matches-interest-profiler-filter"
                            defaultMessage="Interest Profiler"
                          />
                        </label>
                      </div>
                    )}
                    {hasSteadySteadyMatches && (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <RadioButton
                          inputId="matchType3"
                          name="matchType"
                          value="stay steady"
                          checked={matchTypeFilter === "stay steady"}
                          onChange={(e) => {
                            setMatchTypeParam(e.value, "replaceIn")
                            setPageParam(1, "replaceIn")
                          }}
                        />
                        <label htmlFor="matchType3" style={{ cursor: "pointer", marginBottom: 0 }}>
                          {locale === "es-ES" ? "Mantener Rumbo" : "Stay Steady"}
                        </label>
                      </div>
                    )}
                    {hasChangeCourseMatches && (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <RadioButton
                          inputId="matchType4"
                          name="matchType"
                          value="change course"
                          checked={matchTypeFilter === "change course"}
                          onChange={(e) => {
                            setMatchTypeParam(e.value, "replaceIn")
                            setPageParam(1, "replaceIn")
                          }}
                        />
                        <label htmlFor="matchType4" style={{ cursor: "pointer", marginBottom: 0 }}>
                          {locale === "es-ES" ? "Cambiar Rumbo" : "Change Course"}
                        </label>
                      </div>
                    )}
                  </div>
                </div>
            
            </div>

                        <button className="stream-explore-filters-button" onClick={handleFiltersOnClick}>
              <FormattedMessage id="more-filters" defaultMessage="More Filters" />
              <FontAwesomeIcon
                className={`${!showFilters ? "show" : "hide"}`}
                icon={faChevronDown}
                style={{
                  color: "#363636",
                  marginLeft: ".5rem",
                }}
              />
              <FontAwesomeIcon
                className={`${showFilters ? "show" : "hide"}`}
                icon={faChevronUp}
                style={{
                  color: "#363636",
                  marginLeft: ".5rem",
                }}
              />
            </button>
          </div>
        </OutsideClickHandler>

        <div className="ais-Hits">
          <ul className="ais-Hits-list">{FilteredMatchCards}</ul>
        </div>

        <div className="ais-Pagination">
          <Pagination
            activePage={activePage}
            itemsCountPerPage={HITS_PER_PAGE}
            totalItemsCount={filteredMatches.length}
            pageRangeDisplayed={7}
            onChange={handlePageChange}
            activeLinkClass="ais-Pagination-link ais-Pagination-link--selected"
            linkClass="ais-Pagination-link"
            innerClass="ais-Pagination-list"
            itemClass="ais-Pagination-item ais-Pagination-item--page"
            itemClassPrev="ais-Pagination-item ais-Pagination-item--previousPage"
            itemClassNext="ais-Pagination-item ais-Pagination-item--nextPage"
            disabledClass="ais-Pagination-item--disabled"
            hideFirstLastPages
            prevPageText={
              <span className="ais-Pagination-link">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="fas"
                  data-icon="chevron-left"
                  className="svg-inline--fa fa-chevron-left fa-w-10 fa-sm "
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 320 512"
                >
                  <path
                    fill="currentColor"
                    d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"
                  ></path>
                </svg>
              </span>
            }
            nextPageText={
              <span className="ais-Pagination-link">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="fas"
                  data-icon="chevron-right"
                  className="svg-inline--fa fa-chevron-right fa-w-10 fa-sm "
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 320 512"
                >
                  <path
                    fill="currentColor"
                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                  ></path>
                </svg>
              </span>
            }
          />
        </div>
      </div>
      </div>
    </>
  )
}

const salaryOptions = [
  // Very Important, Important, Not that Important
  {
    key: "Not that Important",
    text: <FormattedMessage id="results-filter-not-that-important" defaultMessage="Not that Important" />,
    value: "Not that Important",
  },
  {
    key: "Important",
    text: <FormattedMessage id="results-filter-important" defaultMessage="Important" />,
    value: "Important",
  },
  {
    key: "Very Important",
    text: <FormattedMessage id="results-filter-very-important" defaultMessage="Very Important" />,
    value: "Very Important",
  },
]

const educationOptions = [
  // License, Certificate, Apprenticeship, 2-Year College, 4-Year College, Graduate School
  {
    key: "High School",
    text: <FormattedMessage id="results-filter-high-school" defaultMessage="High School" />,
    value: "High School",
  },
  {
    key: "License",
    text: <FormattedMessage id="results-filter-license" defaultMessage="License" />,
    value: "License",
  },
  {
    key: "Certificate",
    text: <FormattedMessage id="results-filter-certificate" defaultMessage="Certificate" />,
    value: "Certificate",
  },
  {
    key: "Apprenticeship",
    text: <FormattedMessage id="results-filter-apprenticeship" defaultMessage="Apprenticeship" />,
    value: "Apprenticeship",
  },
  {
    key: "2-year College",
    text: <FormattedMessage id="results-filter-2-year-college" defaultMessage="2-year College" />,
    value: "2-year College",
  },
  {
    key: "4-year College",
    text: <FormattedMessage id="results-filter-4-year-college" defaultMessage="4-year College" />,
    value: "4-year College",
  },
  {
    key: "Graduate School",
    text: <FormattedMessage id="results-filter-graduate-school" defaultMessage="Graduate School" />,
    value: "Graduate School",
  },
]

const dressOptions = [
  // Business, Business Casual, Casual
  {
    key: "casual",
    text: <FormattedMessage id="results-filter-casual" defaultMessage="Casual" />,
    value: "casual",
  },
  {
    key: "business casual",
    text: <FormattedMessage id="results-filter-business-casual" defaultMessage="Business Casual" />,
    value: "business casual",
  },
  {
    key: "business",
    text: <FormattedMessage id="results-filter-business" defaultMessage="Business" />,
    value: "business",
  },
  {
    key: "uniform",
    text: <FormattedMessage id="results-filter-uniform" defaultMessage="Uniform" />,
    value: "uniform",
  },
]

const activityOptions = [
  // Stationary, Moderately Active, Active
  {
    key: "stationary",
    text: <FormattedMessage id="results-filter-stationary" defaultMessage="Stationary" />,
    value: "stationary",
  },
  {
    key: "moderately active",
    text: <FormattedMessage id="results-filter-moderately-active" defaultMessage="Moderately Active" />,
    value: "moderately active",
  },
  {
    key: "active",
    text: <FormattedMessage id="results-filter-active" defaultMessage="Active" />,
    value: "active",
  },
]

function scrollToElementWithOffset(element, offset, behavior) {
  if (typeof window === "undefined") return

  const rect = element.getBoundingClientRect()
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const elementTop = rect.top + scrollTop

  // Scroll to the element with the offset
  window.scrollTo({
    top: elementTop - offset,
    behavior: behavior || "smooth",
  })
}

export default CareersPersonalityExplore
